/**
 * Superadmin tenant API — the tenancy app in the LMS, called directly.
 *
 * Shapes mirror what the Django views return, which in turn mirror
 * config/schema/tenant.schema.json. The server validates authoritatively; the
 * client-side checks below only exist so the operator sees a problem while typing.
 */

import { openedx } from '../../shared/lib/openedx';

export type TenantStatus = 'active' | 'planned' | 'suspended' | 'retired';

export interface Tenant {
  slug: string;
  name: string;
  status: TenantStatus;
  is_primary: boolean;
  domains: { site: string; studio: string | null; preview: string | null; aliases: string[] };
  orgs: string[];
  /**
   * Individual courses this tenant may show from orgs it does NOT own.
   *
   * Additive to `orgs` and never overlapping it: the licensing endpoint refuses to store a
   * licence for a tenant that owns the course's org, because that licence would grant
   * nothing and then appear to do nothing when revoked.
   */
  licensed_courses: string[];
  branding: Record<string, unknown>;
  features: Record<string, boolean>;
  copy: Record<string, string>;
  surfaces: Record<string, string>;
}

export interface TenantDraft {
  slug: string;
  name: string;
  status?: TenantStatus;
  domains: { site: string };
  orgs: string[];
  features?: Record<string, boolean>;
}

const BASE = '/api/tenant/v1/tenants';

export const tenantsApi = {
  list: () => openedx.get<Tenant[]>(`${BASE}/`),
  get: (slug: string) => openedx.get<Tenant>(`${BASE}/${slug}`),
  create: (draft: TenantDraft) => openedx.post<Tenant>(`${BASE}/`, draft),
  update: (slug: string, patch: Partial<TenantDraft>) =>
    openedx.patch<Tenant>(`${BASE}/${slug}`, patch),
  /** Suspend, never delete: a tenant owns courses, grades and issued certificates. */
  suspend: (slug: string) => openedx.post<Tenant>(`${BASE}/${slug}/suspend`, {}),
};

/* ------------------------------------------------------------------ licensing ----- */

export interface CourseLicences {
  courseId: string;
  org: string;
  /** Tenants that own the org, so they see the course with or without a licence. */
  owners: string[];
  /** Tenants holding a licence, after the write. */
  licensed: string[];
}

/**
 * Set which tenants may show one course.
 *
 * A PUT of the WHOLE set, not a grant and a revoke: the console asks "which academies get
 * this course", and add/remove calls cannot express "these two and nobody else" without a
 * read-modify-write race between two operators. An empty list means nobody, and revokes
 * every licence on the course.
 *
 * Owners are ignored by the server rather than rejected — see `Tenant.licensed_courses`.
 */
export const setCourseLicences = (courseId: string, slugs: string[]) =>
  openedx.put<CourseLicences>(
    `/api/tenant/v1/courses/${encodeURIComponent(courseId)}/licences`,
    { tenants: slugs },
  );

export function validateDraft(draft: TenantDraft): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(draft.slug)) {
    errors.slug = 'Use lowercase letters, numbers and single hyphens, e.g. northern-campus.';
  }
  if (!draft.name.trim()) {
    errors.name = 'Give the academy a name — learners see this everywhere.';
  }
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(draft.domains.site)) {
    errors.site = 'Enter a hostname, without https:// or a trailing slash.';
  }
  if (draft.orgs.length === 0) {
    errors.orgs = 'At least one organisation code is required — it scopes the catalogue.';
  } else if (draft.orgs.some((org) => !/^[A-Z0-9_]+$/.test(org))) {
    errors.orgs = 'Organisation codes are uppercase letters, numbers and underscores.';
  }

  return errors;
}
